import { tableFromIPC } from 'apache-arrow'
import { fromArrow, ColumnTable, table } from 'arquero'

/**
 * Creates an Arquero DataFrame from Arrow IPC buffer.
 * This is a lightweight wrapper around columnar data (adds ~0-50 MB overhead).
 *
 * NOTE: We do NOT call .reify() here because that would materialize ALL columns
 * into JavaScript arrays, negating the memory savings (~1 GB+ for large datasets).
 * Instead, use getColumnArray() when you need native array methods like .at().
 *
 * @param arrowBuffer - Uint8Array containing Arrow IPC data
 * @returns Arquero ColumnTable (DataFrame) wrapping Arrow vectors
 */
export function createDataFrame(arrowBuffer: Uint8Array): ColumnTable {
  const arrowTable = tableFromIPC(arrowBuffer)
  // @ts-expect-error - Apache Arrow's Table type has slight incompatibility with Arquero's ArrowInput type definition (getChild return type), but works at runtime
  return fromArrow(arrowTable)
}

/**
 * Global cache for column arrays to prevent redundant .toArray() calls.
 * WeakMap automatically releases memory when DataFrame is garbage collected.
 * Key: DataFrame instance -> Map<columnName, cached array>
 */
const columnArrayCache = new WeakMap<ColumnTable, Map<string, unknown[]>>()

/**
 * Extracts a single column as an array with caching.
 * Multiple calls for the same column return the same array reference,
 * preventing memory bloat from redundant .toArray() calls.
 *
 * @param df - Arquero DataFrame
 * @param columnName - Name of column to extract
 * @returns Cached array or Arrow vector
 */
export function getColumn<T = unknown>(df: ColumnTable, columnName: string): T[] {
  const column = df.column(columnName)
  if (!column) return [] as T[]

  // Check cache first
  let dfCache = columnArrayCache.get(df)
  if (!dfCache) {
    dfCache = new Map<string, unknown[]>()
    columnArrayCache.set(df, dfCache)
  }

  const cachedArray = dfCache.get(columnName)
  if (cachedArray) {
    return cachedArray as T[]
  }

  // Extract and cache
  // column.toArray() if available, otherwise column might already be an array
  const array = (column.toArray ? column.toArray() : column) as T[]
  dfCache.set(columnName, array)
  return array
}

/**
 * Gets column names from DataFrame.
 *
 * @param df - Arquero DataFrame
 * @returns Array of column names
 */
export function getColumnNames(df: ColumnTable): string[] {
  return df.columnNames()
}

/**
 * Creates a DataFrame from an array of objects (for testing).
 * This is a convenience function for unit tests that need to create DataFrames
 * from small test data sets.
 *
 * @param data - Array of objects representing rows
 * @returns Arquero ColumnTable (DataFrame)
 *
 * @example
 * const testData = [
 *   { CELL_ID: 'cell1', INSTRUMENT_NAME: 'test1' },
 *   { CELL_ID: 'cell2', INSTRUMENT_NAME: 'test2' }
 * ]
 * const df = createDataFrameFromObjects(testData)
 */
export function createDataFrameFromObjects(data: Record<string, unknown>[]): ColumnTable {
  if (data.length === 0) {
    return table({})
  }

  // Convert array of objects to column-oriented format
  const columnData: Record<string, unknown[]> = {}
  const keys = Object.keys(data[0])

  keys.forEach((key) => {
    columnData[key] = data.map((row) => row[key])
  })

  return table(columnData)
}

